"use client";

import * as React from "react";
import { dbDateToDateOnly, localDateOnlyRangeToUtcRange } from "@ibada/shared-types";
import { type SortingState } from "@tanstack/react-table";
import { useLocation, useNavigate } from "react-router-dom";

import {
  getFieldDefaultValues,
  parseDateValue,
  parseMultiSelectValue,
  serializeMultiSelectValue,
  toDateInputValue,
  type TableFilterFieldConfig,
  type TableFilterValues,
} from "./data-table-filter-types";

export type DataTableQueryParams = {
  q?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  filters?: string;
};

export type DataTableQueryState = {
  search: string;
  filters: TableFilterValues;
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: "asc" | "desc";
  sortingState: SortingState;
  setSearch: (value: string) => void;
  setFilters: (values: TableFilterValues) => void;
  resetFilters: () => void;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  setSortingFromTable: (sorting: SortingState) => void;
  toQueryParams: () => DataTableQueryParams;
};

export type UseDataTableQueryStateOptions = {
  fields?: TableFilterFieldConfig[];
  defaultSearch?: string;
  defaultPage?: number;
  defaultLimit?: number;
  defaultSortBy?: string;
  defaultSortOrder?: "asc" | "desc";
  urlMode?: "replace" | "push";
};

const SEARCH_PARAM = "q";
const PAGE_PARAM = "page";
const LIMIT_PARAM = "limit";
const SORT_BY_PARAM = "sortBy";
const SORT_ORDER_PARAM = "sortOrder";
const FILTERS_PARAM = "filters";

function parsePositiveInteger(value: string | null): number | null {
  if (!value) return null;
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed < 1) return null;
  return parsed;
}

function normalizeString(value: string | null | undefined): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function parseFiltersParam(value: string | null): Record<string, unknown> {
  if (!value) return {};
  const trimmed = value.trim();
  if (!trimmed) return {};
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    return {};
  }
  return {};
}

function buildFilterValues(
  fields: TableFilterFieldConfig[],
  record: Record<string, unknown>
): TableFilterValues {
  const values: TableFilterValues = {};

  for (const field of fields) {
    const defaults = getFieldDefaultValues(field);
    for (const key of Object.keys(defaults)) {
      values[key] = defaults[key] ?? "";
    }

    if (field.type === "dateRange") {
      const fromRaw = record[field.fromKey];
      const toRaw = record[field.toKey];
      const fromDate = typeof fromRaw === "string" ? parseDateValue(fromRaw) : undefined;
      const toDate = typeof toRaw === "string" ? parseDateValue(toRaw) : undefined;
      if (fromDate) values[field.fromKey] = toDateInputValue(fromDate);
      if (toDate) values[field.toKey] = toDateInputValue(toDate);
      continue;
    }

    if (field.type === "numberRange") {
      const minRaw = record[field.minKey];
      const maxRaw = record[field.maxKey];
      if (typeof minRaw === "number" && Number.isFinite(minRaw)) {
        values[field.minKey] = String(minRaw);
      } else if (typeof minRaw === "string") {
        values[field.minKey] = minRaw.trim();
      }

      if (typeof maxRaw === "number" && Number.isFinite(maxRaw)) {
        values[field.maxKey] = String(maxRaw);
      } else if (typeof maxRaw === "string") {
        values[field.maxKey] = maxRaw.trim();
      }
      continue;
    }

    if (field.type === "multiSelect") {
      const raw = record[field.key];
      if (Array.isArray(raw)) {
        const next = raw
          .map((item) => (typeof item === "string" ? item.trim() : ""))
          .filter(Boolean);
        values[field.key] = serializeMultiSelectValue(next);
      }
      continue;
    }

    if (field.type === "boolean") {
      const raw = record[field.key];
      if (typeof raw === "boolean") {
        values[field.key] = raw ? "true" : "false";
      } else if (typeof raw === "string") {
        const normalized = raw.trim().toLowerCase();
        if (normalized === "true" || normalized === "false") {
          values[field.key] = normalized;
        }
      }
      continue;
    }

    if (field.type === "dateTime") {
      const raw = record[field.key];
      const parsed = typeof raw === "string" ? parseDateValue(raw) : undefined;
      if (parsed) {
        values[field.key] = toDateInputValue(parsed);
      }
      continue;
    }

    const rawValue = record[field.key];
    if (typeof rawValue === "string") {
      values[field.key] = rawValue;
    }
  }

  return values;
}

function serializeFilters(
  fields: TableFilterFieldConfig[],
  values: TableFilterValues
): Record<string, unknown> {
  const output: Record<string, unknown> = {};
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  for (const field of fields) {
    if (field.type === "dateRange") {
      const fromValue = values[field.fromKey];
      const toValue = values[field.toKey];
      const fromDate = dbDateToDateOnly(fromValue);
      const toDate = dbDateToDateOnly(toValue);
      if (fromDate) {
        output[field.fromKey] = localDateOnlyRangeToUtcRange({ from: fromDate, to: fromDate, timeZone }).from;
      }
      if (toDate) {
        output[field.toKey] = localDateOnlyRangeToUtcRange({ from: toDate, to: toDate, timeZone }).to;
      }
      continue;
    }

    if (field.type === "numberRange") {
      const minValue = values[field.minKey];
      const maxValue = values[field.maxKey];
      if (typeof minValue === "string" && minValue.trim().length > 0) {
        const parsed = Number(minValue);
        output[field.minKey] = Number.isFinite(parsed) ? parsed : minValue.trim();
      }

      if (typeof maxValue === "string" && maxValue.trim().length > 0) {
        const parsed = Number(maxValue);
        output[field.maxKey] = Number.isFinite(parsed) ? parsed : maxValue.trim();
      }
      continue;
    }

    if (field.type === "multiSelect") {
      const parsed = parseMultiSelectValue(values[field.key] ?? "");
      if (parsed.length > 0) output[field.key] = parsed;
      continue;
    }

    if (field.type === "boolean") {
      const rawValue = values[field.key];
      if (rawValue === "true") output[field.key] = true;
      if (rawValue === "false") output[field.key] = false;
      continue;
    }

    if (field.type === "dateTime") {
      const rawValue = values[field.key];
      const dateOnly = dbDateToDateOnly(rawValue);
      if (dateOnly) {
        output[field.key] = localDateOnlyRangeToUtcRange({ from: dateOnly, to: dateOnly, timeZone }).from;
      }
      continue;
    }

    const rawValue = values[field.key];
    if (typeof rawValue === "string" && rawValue.trim().length > 0) {
      output[field.key] = rawValue;
    }
  }

  return output;
}

export function useDataTableQueryState(options: UseDataTableQueryStateOptions = {}): DataTableQueryState {
  const fields = React.useMemo(() => options.fields ?? [], [options.fields]);
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const searchParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);

  const defaultPage = options.defaultPage ?? 1;
  const defaultLimit = options.defaultLimit ?? 20;
  const defaultSortBy = options.defaultSortBy ?? "";
  const defaultSortOrder = options.defaultSortOrder ?? "desc";
  const defaultSearch = options.defaultSearch ?? "";

  const search = normalizeString(searchParams.get(SEARCH_PARAM)) ?? defaultSearch;
  const page = parsePositiveInteger(searchParams.get(PAGE_PARAM)) ?? defaultPage;
  const limit = parsePositiveInteger(searchParams.get(LIMIT_PARAM)) ?? defaultLimit;
  const sortBy = normalizeString(searchParams.get(SORT_BY_PARAM)) ?? defaultSortBy;
  const sortOrderParam = normalizeString(searchParams.get(SORT_ORDER_PARAM));
  const sortOrder = sortOrderParam === "asc" || sortOrderParam === "desc" ? sortOrderParam : defaultSortOrder;
  const filtersRecord = parseFiltersParam(searchParams.get(FILTERS_PARAM));
  const filters = buildFilterValues(fields, filtersRecord);

  const sortingState: SortingState = sortBy
    ? [{ id: sortBy, desc: sortOrder === "desc" }]
    : [];

  const buildQueryParams = (next: {
    nextSearch?: string;
    nextPage?: number;
    nextLimit?: number;
    nextSortBy?: string;
    nextSortOrder?: "asc" | "desc";
    nextFilters?: TableFilterValues;
  }) => {
    const nextSearch = next.nextSearch ?? search;
    const nextPage = next.nextPage ?? page;
    const nextLimit = next.nextLimit ?? limit;
    const nextSortBy = next.nextSortBy ?? sortBy;
    const nextSortOrder = next.nextSortOrder ?? sortOrder;
    const nextFilters = next.nextFilters ?? filters;

    const params = new URLSearchParams(searchParams.toString());

    if (nextSearch.trim()) params.set(SEARCH_PARAM, nextSearch.trim());
    else params.delete(SEARCH_PARAM);

    params.set(PAGE_PARAM, String(nextPage));
    params.set(LIMIT_PARAM, String(nextLimit));

    if (nextSortBy.trim()) {
      params.set(SORT_BY_PARAM, nextSortBy.trim());
      params.set(SORT_ORDER_PARAM, nextSortOrder);
    } else {
      params.delete(SORT_BY_PARAM);
      params.delete(SORT_ORDER_PARAM);
    }

    const serializedFilters = serializeFilters(fields, nextFilters);
    if (Object.keys(serializedFilters).length > 0) {
      params.set(FILTERS_PARAM, JSON.stringify(serializedFilters));
    } else {
      params.delete(FILTERS_PARAM);
    }

    return params;
  };

  const updateParams = (next: {
    nextSearch?: string;
    nextPage?: number;
    nextLimit?: number;
    nextSortBy?: string;
    nextSortOrder?: "asc" | "desc";
    nextFilters?: TableFilterValues;
  }) => {
    const params = buildQueryParams(next);
    const nextQuery = params.toString();
    const currentQuery = searchParams.toString();
    if (nextQuery === currentQuery) return;
    const href = nextQuery ? `${pathname}?${nextQuery}` : pathname;
    navigate(href, { replace: options.urlMode !== "push", preventScrollReset: true });
  };

  const setSearch = React.useCallback(
    (value: string) => {
      updateParams({ nextSearch: value, nextPage: defaultPage });
    },
    [defaultPage, updateParams],
  );

  const setFilters = React.useCallback(
    (values: TableFilterValues) => {
      updateParams({ nextFilters: values, nextPage: defaultPage });
    },
    [defaultPage, updateParams],
  );

  const resetFilters = React.useCallback(() => {
    const resetValues: TableFilterValues = {};
    for (const field of fields) {
      const defaults = getFieldDefaultValues(field);
      for (const key of Object.keys(defaults)) {
        resetValues[key] = defaults[key] ?? "";
      }
    }
    updateParams({ nextFilters: resetValues, nextPage: defaultPage });
  }, [defaultPage, fields, updateParams]);

  const setPage = React.useCallback(
    (nextPage: number) => {
      updateParams({ nextPage: Math.max(1, Math.trunc(nextPage)) });
    },
    [updateParams],
  );

  const setLimit = React.useCallback(
    (nextLimit: number) => {
      const normalized = Math.max(1, Math.trunc(nextLimit));
      updateParams({ nextLimit: normalized, nextPage: defaultPage });
    },
    [defaultPage, updateParams],
  );

  const setSortingFromTable = React.useCallback(
    (nextSorting: SortingState) => {
      if (!nextSorting.length) {
        updateParams({ nextSortBy: "", nextSortOrder: defaultSortOrder, nextPage: defaultPage });
        return;
      }
      const primary = nextSorting[0];
      if (!primary) return;
      updateParams({
        nextSortBy: primary.id,
        nextSortOrder: primary.desc ? "desc" : "asc",
        nextPage: defaultPage,
      });
    },
    [defaultPage, defaultSortOrder, updateParams],
  );

  const toQueryParams = React.useCallback((): DataTableQueryParams => {
    const serializedFilters = serializeFilters(fields, filters);
    const filtersString = Object.keys(serializedFilters).length > 0
      ? JSON.stringify(serializedFilters)
      : undefined;

    return {
      q: search.trim() ? search.trim() : undefined,
      page,
      limit,
      sortBy: sortBy.trim() ? sortBy.trim() : undefined,
      sortOrder: sortBy.trim() ? sortOrder : undefined,
      filters: filtersString,
    };
  }, [fields, filters, limit, page, search, sortBy, sortOrder]);

  return {
    search,
    filters,
    page,
    limit,
    sortBy,
    sortOrder,
    sortingState,
    setSearch,
    setFilters,
    resetFilters,
    setPage,
    setLimit,
    setSortingFromTable,
    toQueryParams,
  };
}

export { serializeFilters };
